<script setup lang="ts">
import { vOnClickOutside } from "@vueuse/components";

const localePath = useLocalePath();

const { state } = useAppStore();

const { data: countries } = await useFetch<any>("/api/data/countries");
const { data: positions } = await useFetch<any>("/api/data/positions");

const companyTypes = [
  {
    label: "Medium and large companies",
    value: 0,
    color: "#360086",
  },
  {
    label: "Small companies",
    value: 1,
    color: "#FFC14A",
  },
  {
    label: "Retailers",
    value: 2,
    color: "#79DD7D",
  },
];

const ths = ref([
  {
    key: "company",
    label: useText(state.strings, "sc_company"),
    type_of: "string",
    sort_order: "",
    isCompany: true,
    isCat: false,
  },
  {
    key: "tr",
    index: 0,
    label: useCategoryName(state.categories, 1),
    type_of: "number",
    sort_order: "",
    isCat: true,
  },
  {
    key: "li",
    index: 1,
    label: useCategoryName(state.categories, 2),
    type_of: "number",
    sort_order: "",
    isCat: true,
  },
  {
    index: 2,
    label: useCategoryName(state.categories, 3),
    type_of: "number",
    sort_order: "",
    isCat: true,
  },
  {
    index: 3,
    label: useCategoryName(state.categories, 4),
    type_of: "number",
    sort_order: "",
    isCat: true,
  },
  {
    index: 4,
    label: useCategoryName(state.categories, 5),
    type_of: "number",
    sort_order: "",
    isCat: true,
  },
  {
    index: 5,
    label: useCategoryName(state.categories, 6),
    type_of: "number",
    sort_order: "",
    isCat: true,
  },
  {
    index: 6,
    label: useCategoryName(state.categories, 8),
    type_of: "number",
    sort_order: "",
    isCat: true,
  },
  {
    key: "ov",
    index: 8,
    label: useText(state.strings, "sc_cat_ov"),
    type_of: "number",
    sort_order: "",
    isCat: true,
  },
  {
    index: 8,
    label: "cat_link",
    isCat: false,
  },
]);
const scoreCols = 9;
const companyType = ref(0);
const sortable = ref<{
  key: string | number;
  direction: "asc" | "desc" | "";
}>({
  key: "company",
  direction: "asc",
});
const search = ref("");
const searchResults = ref<any[]>([]);
const searchResultsVisible = ref(false);
const searchCompany = ref("");
const filterCountry = ref("");
const filterPosition = ref("");
const filterKey = ref("");

const filterKeys = ref([
  {
    label: useTresholdName(state.tresholds, 1),
    value: "ge75",
  },
  {
    label: useTresholdName(state.tresholds, 2),
    value: "ge50l75",
  },
  {
    label: useTresholdName(state.tresholds, 3),
    value: "ge25l50",
  },
  {
    label: useTresholdName(state.tresholds, 4),
    value: "ge0l25",
  },
  {
    label: useTresholdName(state.tresholds, 5),
    value: "non",
  },
]);

const { data: scorecards, status } = await useFetch<any>(
  () =>
    `/api/data/company?country=${filterCountry.value}&position=${filterPosition.value}&score=${filterKey.value}`,
);
const { data: scorecardsUnmutated } = await useFetch<any>(() => `/api/data/company`);

const companies = ref([
  ...scorecards.value[companyType.value].companies,
  ...scorecards.value[companyType.value].non_participants,
]);

const companiesUnmutated = ref([
  ...scorecardsUnmutated.value[companyType.value].companies,
  ...scorecardsUnmutated.value[companyType.value].non_participants,
]);

const onSort = (key: string | number) => {
  companies.value = [
    ...scorecards.value[companyType.value].companies,
    ...scorecards.value[companyType.value].non_participants,
  ];

  if (sortable.value.key === key) {
    sortable.value.direction = sortable.value.direction === "asc" ? "desc" : "asc";
  } else {
    sortable.value.key = key;
    sortable.value.direction = "asc";
  }

  if (key === "company") {
    companies.value.sort((a: any, b: any) => {
      return sortable.value.direction === "asc"
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    });
  } else {
    // Sort by scores index with key score
    companies.value.sort((a: any, b: any) => {
      return sortable.value.direction === "asc"
        ? a.scores[key].score - b.scores[key].score
        : b.scores[key].score - a.scores[key].score;
    });
  }
};

const onCompanyTypeChange = (value: number) => {
  companyType.value = value;
  companies.value = [
    ...scorecards.value[companyType.value].companies,
    ...scorecards.value[companyType.value].non_participants,
  ];
  console.log("Scorecards", scorecards.value);
  console.log("Companies", companies.value);
};

const onSearch = () => {
  const searchTerm = search.value.toLowerCase();

  searchResults.value = companiesUnmutated.value.filter((company: any) => {
    if (company.name.toLowerCase().includes(searchTerm)) {
      return company;
    }
    if (company.product_lines) {
      if (company.product_lines.toLowerCase().includes(searchTerm)) {
        return company;
      }
    }
  });
};

const onSearchCompany = () => {
  companies.value = [
    ...scorecards.value[companyType.value].companies,
    ...scorecards.value[companyType.value].non_participants,
  ].filter((company: any) =>
    company.name.toLowerCase().includes(searchCompany.value.toLowerCase()),
  );
};

watch(
  () => scorecards.value,
  () => {
    companies.value = [
      ...scorecards.value[companyType.value].companies,
      ...scorecards.value[companyType.value].non_participants,
    ];
  },
);

onMounted(() => {
  onSearch();
});
</script>

<template>
  <div v-if="scorecards">
    <UiPageHeader
      :eyebrow="useText(state.strings, 'global_edition')"
      :title="useText(state.strings, 'sc')"
    />
    <UiScorecardsKeys class="pt-20" />
    <ClientOnly>
      <div class="py-12">
        <UContainer>
          <div v-on-click-outside="() => (searchResultsVisible = false)" class="relative z-1">
            <UInput
              v-model="search"
              :autofocus="false"
              :placeholder="useText(state.strings, 'sc_filters_search_placeholder')"
              icon="i-lucide-search"
              size="xl"
              variant="outline"
              class="w-full text-chsc-indigo-500"
              @update:model-value="onSearch"
              @focus="searchResultsVisible = true"
            />
            <ul
              v-if="searchResults.length > 0 && searchResultsVisible"
              class="absolute top-full left-1/2 w-[calc(100%-50px)] max-h-[25vh] overflow-auto -translate-x-1/2 bg-white shadow-lg border border-chsc-indigo-500 rounded-b-lg"
            >
              <li v-for="company in searchResults" :key="company.id">
                <NuxtLink
                  :to="`/scorecards/${company.id}`"
                  class="w-full px-4 py-2 fs-xs hover:bg-chsc-indigo-50 hover:text-white flex flex-col gap-1"
                >
                  <span class="font-bold">
                    {{ company.name }}
                  </span>
                  <span class="fs-xs">
                    {{ company.product_lines }}
                  </span>
                </NuxtLink>
              </li>
            </ul>
          </div>
          <div class="mt-4">
            <UInput
              v-model="searchCompany"
              :placeholder="useText(state.strings, 'sc_filters_search_filter_placeholder')"
              icon="i-lucide-search"
              size="xl"
              variant="outline"
              class="w-full text-chsc-indigo-500"
              @update:model-value="onSearchCompany"
            />
          </div>
          <div
            class="mt-4 flex space-x-4 [&>div]:w-1/3 max-[641px]:flex-col max-[641px]:space-x-0 max-[641px]:space-y-4 max-[641px]:[&>div]:w-full"
          >
            <div>
              <USelectMenu
                v-model="filterCountry"
                value-key="id"
                label-key="value"
                icon="i-lucide-filter"
                :items="countries"
                :placeholder="useText(state.strings, 'sc_filters_country')"
                size="xl"
                class="w-full h-full"
              >
                <template #trailing>
                  <div class="flex items-center gap-2">
                    <UButton
                      v-if="filterCountry"
                      icon="i-lucide-x"
                      variant="ghost"
                      size="xs"
                      @click.stop="filterCountry = ''"
                    />
                    <UIcon
                      v-else
                      name="i-lucide-chevron-down"
                      class="text-[var(--ui-text-dimmed)]"
                    />
                  </div>
                </template>
              </USelectMenu>
            </div>
            <div>
              <USelectMenu
                v-model="filterPosition"
                value-key="id"
                label-key="value"
                icon="i-lucide-filter"
                :items="positions"
                :placeholder="useText(state.strings, 'sc_filters_position')"
                size="xl"
                class="w-full h-full"
              >
                <template #trailing>
                  <div class="flex items-center gap-2">
                    <UButton
                      v-if="filterPosition"
                      icon="i-lucide-x"
                      variant="ghost"
                      size="xs"
                      @click.stop="filterPosition = ''"
                    />
                    <UIcon
                      v-else
                      name="i-lucide-chevron-down"
                      class="text-[var(--ui-text-dimmed)]"
                    />
                  </div>
                </template>
              </USelectMenu>
            </div>
            <div>
              <USelectMenu
                v-model="filterKey"
                value-key="value"
                label-key="label"
                icon="i-lucide-filter"
                :items="filterKeys"
                :placeholder="useText(state.strings, 'sc_filters_key')"
                size="xl"
                class="w-full h-full"
              >
                <template #trailing>
                  <div class="flex items-center gap-2">
                    <UButton
                      v-if="filterKey"
                      icon="i-lucide-x"
                      variant="ghost"
                      size="xs"
                      @click.stop="filterKey = ''"
                    />
                    <UIcon
                      v-else
                      name="i-lucide-chevron-down"
                      class="text-[var(--ui-text-dimmed)]"
                    />
                  </div>
                </template>
              </USelectMenu>
            </div>
          </div>
        </UContainer>
      </div>
      <div class="flex max-[641px]:flex-col">
        <button
          v-for="(typeCompany, index) in companyTypes"
          :key="index"
          class="flex items-center gap-4 px-8 py-2 cursor-pointer group"
          :style="{
            backgroundColor: typeCompany.color,
            color: typeCompany.value == 0 ? '#fff' : '#360086',
          }"
          @click="onCompanyTypeChange(typeCompany.value)"
        >
          <span
            class="font-bold fs-base group-hover:opacity-80"
            :style="{ textDecoration: typeCompany.value == companyType ? 'underline' : 'none' }"
            >{{ typeCompany.label }}</span
          >
          <span
            class="w-[25px] h-[25px] rounded-full flex items-center justify-center border fs-xs"
            >{{ scorecards[index].count }}</span
          >
        </button>
      </div>
      <div class="overflow-x-auto">
        <table class="w-full table-fixed">
          <thead>
            <tr class="text-white" :style="{ backgroundColor: companyTypes[companyType].color }">
              <th
                v-for="th in ths"
                :key="th.label"
                :class="[
                  th.isCompany ? 'w-[200px]' : th.label === 'cat_link' ? 'w-[80px]' : '',
                  'px-3.5 py-2.5',
                  { 'max-[1025px]:hidden': th.index >= 0 && th.index < 7 },
                ]"
                :style="{ color: companyTypes[companyType].value == 0 ? '#fff' : '#360086' }"
              >
                <button
                  v-if="th.label !== 'cat_link'"
                  class="flex items-center gap-2 fs-xs uppercase font-normal w-full cursor-pointer"
                  :style="{ textAlign: th.isCat ? 'center' : 'left' }"
                  @click="onSort(th.isCat ? th.index : 'company')"
                >
                  <span class="block w-full whitespace-pre-wrap">{{ th.label }}</span>
                  <span class="flex flex-col items-center flex-shrink-0">
                    <UIcon
                      name="i-lucide-chevron-up"
                      :class="[
                        'opacity-50',
                        {
                          'opacity-100': th.isCat
                            ? sortable.key === th.index && sortable.direction === 'asc'
                            : sortable.key === 'sc_company' &&
                              sortable.direction === 'asc' &&
                              th.isCompany,
                        },
                      ]"
                    />
                    <UIcon
                      name="i-lucide-chevron-down"
                      :class="[
                        'opacity-50',
                        {
                          'opacity-100': th.isCat
                            ? sortable.key === th.index && sortable.direction === 'desc'
                            : sortable.key === 'sc_company' &&
                              sortable.direction === 'desc' &&
                              th.isCompany,
                        },
                      ]"
                    />
                  </span>
                </button>
              </th>
            </tr>
          </thead>
          <tbody v-if="status != 'pending'">
            <tr
              v-for="company in companies"
              :key="company.id"
              class="[&>td]:px-3.5 [&>td]:py-6 even:bg-[#F8F5EE]"
            >
              <td>
                <span class="font-semibold">{{ company.name }}</span>
              </td>
              <template v-for="i in scoreCols" :key="i">
                <td v-if="i != 9" :class="[{ 'max-[1025px]:hidden': i >= 0 && i < 8 }]">
                  <div class="flex items-center justify-center relative">
                    <span
                      v-if="company.enabled"
                      :class="[
                        'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-semibold',
                        { 'text-base': i === 8, 'text-sm': i !== 8 },
                      ]"
                      :style="{
                        color:
                          i == 8
                            ? useScoreTreshold(company.scores[8].over_100, false).dark
                            : useScoreTreshold(company.scores[i - 1].over_100, false).dark,
                      }"
                    >
                      {{
                        i == 8
                          ? company.scores[8].over_100.toFixed(0)
                          : useFormatScore(company.scores[i - 1].over_100).toFixed(0)
                      }}%
                    </span>
                    <!-- <img
                                            v-if="company.enabled && i != 8"
                                            :src="`/img/treshold-${useScoreTreshold(company.scores[i-1].over_5, true).treshold}.svg`"
                                            :class="[
                                                i === 8 ? 'w-[65px]' : 'w-[48px]',
                                            ]"
                                        > -->
                    <img
                      v-if="company.enabled"
                      :src="`/img/treshold-empty-${i == 8 ? useScoreTreshold(company.scores[8].over_5, true).treshold : useScoreTreshold(company.scores[i - 1].over_5, true).treshold}.svg`"
                      :class="[i === 8 ? 'w-[65px]' : 'w-[48px]']"
                    />
                    <img
                      v-else
                      :src="`/img/treshold-5.svg`"
                      :class="[i === 8 ? 'w-[65px]' : 'w-[48px]']"
                    />
                  </div>
                </td>
              </template>
              <td class="py-4 px-6">
                <div class="flex items-center justify-center border-l border-chsc-border pl-3">
                  <UButton
                    :to="localePath(`/scorecards/${company.id}`)"
                    icon="i-lucide-move-right"
                    variant="outline"
                    size="xl"
                  />
                </div>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td colspan="9" class="p-12">
                <div class="flex flex-col gap-4 items-center justify-center">
                  <p class="animate-pulse text-xs text-chsc-indigo-500">
                    {{ useText(state.strings, "sc_loading") }}
                  </p>
                  <UProgress animation="swing" />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </ClientOnly>
  </div>
</template>
